import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { FinalCTA } from "@/components/home/FinalCTA";
import { SEO } from "@/components/SEO";
import { faqSchema } from "@/lib/structuredData";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Calculator,
  Users,
  FileText,
  Building2,
  Receipt,
  ClipboardList,
  UserCheck,
  Shield,
  HelpCircle,
} from "lucide-react";

const services = [
  {
    icon: Calculator,
    title: "Monthly Bookkeeping",
    description: "Accurate recording of all your financial transactions. We process invoices, receipts, bank statements, and keep your books up to date every month.",
  },
  {
    icon: Users,
    title: "Payroll (EMP201, EMP501)",
    description: "Complete payroll processing including monthly EMP201 submissions and bi-annual EMP501 reconciliations. We ensure your employees are paid correctly and on time.",
  },
  {
    icon: Receipt,
    title: "UIF Monthly Declarations",
    description: "Timely submission of UIF declarations to the Department of Labour. We handle the paperwork so you stay compliant with employment legislation.",
  },
  {
    icon: FileText,
    title: "Annual Financial Statements",
    description: "Professionally prepared annual financial statements that meet SARS requirements and give you a clear picture of your business performance.",
  },
  {
    icon: ClipboardList,
    title: "Annual Tax Returns",
    description: "We prepare and submit your annual income tax returns, ensuring all deductions are claimed and you remain SARS compliant.",
  },
  {
    icon: ClipboardList,
    title: "Provisional Tax Returns",
    description: "Two provisional tax submissions per year to keep you ahead of your tax obligations and avoid penalties.",
  },
  {
    icon: Building2,
    title: "CIPC Annual Returns",
    description: "On-time submission of your company's annual return to CIPC to maintain your business's good standing.",
  },
  {
    icon: UserCheck,
    title: "Beneficial Ownership (BO) Submission",
    description: "Compliance with beneficial ownership register requirements as mandated by CIPC regulations.",
  },
  {
    icon: Shield,
    title: "Workmen's Compensation",
    description: "Annual return submissions to the Compensation Fund for businesses that need to comply with COIDA requirements.",
  },
  {
    icon: HelpCircle,
    title: "Custom Quotes & Advisory",
    description: "Need something specific? We offer custom solutions and advisory services tailored to your unique business needs.",
  },
];

const faqs = [
  {
    question: "What information do you need to start?",
    answer: "We'll need your company registration documents, bank statements, previous financial records (if available), employee details for payroll, and access to any accounting software you currently use. We'll guide you through the onboarding process step by step.",
  },
  {
    question: "Do you work remotely across South Africa?",
    answer: "Yes! We serve clients throughout South Africa remotely. Our cloud-based systems allow us to work efficiently with businesses in any province, from Cape Town to Johannesburg and everywhere in between.",
  },
  {
    question: "What software do you use?",
    answer: "We work with popular accounting platforms including Xero, QuickBooks, Sage, and others. If you already have a system in place, we can likely integrate with it. If not, we'll recommend the best solution for your needs.",
  },
  {
    question: "When are monthly deadlines?",
    answer: "EMP201 submissions are due by the 7th of each month. UIF declarations are also monthly. We'll provide you with a clear compliance calendar so you always know what's coming up.",
  },
  {
    question: "Can you help if my books are behind?",
    answer: "Absolutely! We regularly help clients catch up on backlog bookkeeping. We'll assess your situation, provide a quote for the catch-up work, and get you current as quickly as possible.",
  },
];

const Services = () => {
  return (
    <Layout>
      <SEO 
        title="Bookkeeping, Payroll & SARS Compliance Services | South Africa"
        description="Professional bookkeeping, payroll processing, EMP201, EMP501, UIF declarations, annual tax returns, and CIPC compliance. Serving businesses across South Africa."
        canonical="/services"
        structuredData={faqSchema}
      />
      {/* Hero */}
      <section className="py-16 lg:py-24 section-accent">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-6">
              Our Services
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Comprehensive bookkeeping, payroll, and compliance services designed to keep 
              your South African business running smoothly and SARS-ready.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 lg:py-28 section-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {services.map((service) => (
              <div key={service.title} className="card-elevated p-8">
                <div className="icon-container mb-6">
                  <service.icon className="w-6 h-6" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-foreground mb-3">
                  {service.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed text-sm">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 lg:py-28 section-light">
        <div className="container mx-auto px-4">
          <SectionHeader
            label="FAQ"
            title="Frequently Asked Questions"
            description="Got questions? We've got answers. Here are some common queries from our clients."
          />

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-card border border-border rounded-lg px-6"
                >
                  <AccordionTrigger className="text-left font-heading text-lg font-semibold hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <FinalCTA />
    </Layout>
  );
};

export default Services;
